/**
 * File uploads: the three-step flow from client-api.md, and the tray that
 * shows it happening.
 *
 * 1. `POST /rooms/:id/uploads` reserves the attachment and charges quota, so a
 *    full disk is reported before a single byte moves.
 * 2. `PUT` to the returned URL, which is the credential and therefore carries
 *    no relaii cookies of its own.
 * 3. `POST /uploads/:id/finalize` has the server confirm the bytes arrived.
 *
 * The attachment id from step 1 is what the composer passes as
 * `attachment_ids` when it posts the message.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement } from 'react'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import type { Attachment, FinalizeUploadRequest, UploadSession } from '@/api/types'
import { Button, IconButton, Spinner } from '@/components/ui'
import { formatBytes } from '@/lib/format'
import styles from './upload.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values
    .filter((value): value is string => typeof value === 'string' && value !== '')
    .join(' ')
}

// ── Model ───────────────────────────────────────────────────────────────────

export type UploadState =
  | 'queued'
  | 'creating'
  | 'uploading'
  | 'finalizing'
  | 'ready'
  | 'failed'
  | 'cancelled'

export interface UploadItem {
  /** Local to this tray. The server's id lives on `attachment`. */
  id: string
  file: File
  name: string
  size: number
  mimeType: string
  state: UploadState
  /** 0..1. Meaningful from `uploading` onwards. */
  progress: number
  /** Set from step 1; `status` becomes `ready` or `scanning` after step 3. */
  attachment: Attachment | null
  error: string | null
  /** Whether trying again could plausibly work. False for an oversized file. */
  retryable: boolean
  /**
   * Set when the bytes are stored and only the server's confirmation is
   * missing. Trying again then repeats step 3 alone, not the transfer.
   */
  resume: 'finalize' | null
}

export interface UploadsApi {
  items: UploadItem[]
  /** Attachment ids to pass as `attachment_ids` when posting the message. */
  readyIds: string[]
  /** True while anything is still in flight. */
  busy: boolean
  /**
   * True when a file in the tray will not be attached as things stand — it
   * failed, or its transfer was cancelled. Sending would silently leave it
   * out, so the composer holds the message back until it is retried or removed.
   */
  failed: boolean
  addFiles(files: FileList | readonly File[]): void
  /** Aborts an in-flight transfer but keeps the entry so it can be retried. */
  cancel(id: string): void
  retry(id: string): void
  /** Aborts if necessary and drops the entry entirely. */
  remove(id: string): void
  /** Drops everything. Call this once the message has been posted. */
  clear(): void
}

const ACTIVE_STATES: ReadonlySet<UploadState> = new Set<UploadState>([
  'queued',
  'creating',
  'uploading',
  'finalizing',
])

/**
 * Hashing means reading the whole file into memory, which is fine for the
 * documents and screenshots that make up almost every chat attachment and not
 * fine for a video. Above the cap the upload is finalised without a checksum,
 * which the contract allows; the server still verifies the size.
 */
const CHECKSUM_LIMIT_BYTES = 64 * 1024 * 1024

/**
 * Finalize has the server read the whole object back — size, checksum, the
 * virus scan — which for a large file on object storage takes longer than an
 * ordinary request is allowed. Timing it out would only throw away a transfer
 * that had already succeeded.
 */
export const FINALIZE_TIMEOUT_MS = 120_000

let nextLocalId = 0

function localId(): string {
  nextLocalId += 1
  return `upload-${nextLocalId}`
}

/** A file dragged from some systems arrives with no type at all. */
function mimeTypeOf(file: File): string {
  return file.type === '' ? 'application/octet-stream' : file.type
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

/**
 * A 4xx is the server's decision about the file — too large, refused by the
 * scanner, a session that has expired — and posting the same finalize again
 * only asks for the same answer. 408 and 429 ask for a retry in as many words.
 */
function isRefusal(error: unknown): boolean {
  if (!isApiError(error)) return false
  if (error.status === 408 || error.status === 429) return false
  return error.status >= 400 && error.status < 500
}

/**
 * The local Vite server proxies `/files` to Phoenix. Phoenix still returns an
 * absolute URL for its local storage backend, though, and a direct PUT from
 * port 5173 to port 4000 would be blocked by the browser's CORS rules. Keep
 * production and object-storage URLs untouched; only route Phoenix's local
 * development endpoint through the existing Vite proxy.
 */
function uploadTarget(url: string): string {
  if (!import.meta.env.DEV || typeof window === 'undefined') return url

  const target = new URL(url, window.location.href)
  if (!target.pathname.startsWith('/files/v1/')) return url
  return `${target.pathname}${target.search}`
}

function readNumber(details: Record<string, unknown>, key: string): number | null {
  const value = details[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function toHex(buffer: ArrayBuffer): string {
  let hex = ''
  for (const byte of new Uint8Array(buffer)) {
    hex += byte.toString(16).padStart(2, '0')
  }
  return hex
}

async function computeChecksum(file: File, signal: AbortSignal): Promise<string | null> {
  if (file.size > CHECKSUM_LIMIT_BYTES) return null

  // Absent outside a secure context, which a self-hosted server on plain HTTP
  // in a lab will be. The upload should still work there.
  const subtle: SubtleCrypto | undefined = globalThis.crypto?.subtle
  if (subtle === undefined) return null

  try {
    const bytes = await file.arrayBuffer()
    if (signal.aborted) return null
    return `sha256:${toHex(await subtle.digest('SHA-256', bytes))}`
  } catch {
    // An unreadable file or an unavailable digest is not worth failing an
    // otherwise complete upload over.
    return null
  }
}

/**
 * `quota_exceeded` is the local storage backend saying the disk is full. A
 * generic "upload failed" would leave the user retrying forever, so the
 * numbers the server sent are spelled out instead.
 */
function describeQuota(details: Record<string, unknown>, file: File): string {
  const available = readNumber(details, 'available_bytes')
  const limit = readNumber(details, 'limit_bytes')
  const used = readNumber(details, 'used_bytes')

  const parts = [`There is not enough space left on the server for ${formatBytes(file.size)}.`]

  if (available !== null) {
    parts.push(`${formatBytes(Math.max(available, 0))} free`)
    if (limit !== null) parts.push(`of ${formatBytes(limit)}`)
    parts.push('— ask an administrator to free some room.')
  } else if (used !== null && limit !== null) {
    parts.push(`${formatBytes(used)} of ${formatBytes(limit)} is in use`)
    parts.push('— ask an administrator to free some room.')
  } else {
    parts.push('Ask an administrator to free some room.')
  }

  return parts.join(' ')
}

function describeFailure(error: unknown, file: File): string {
  if (isApiError(error)) {
    switch (error.code) {
      case 'quota_exceeded':
        return describeQuota(error.details, file)
      case 'payload_too_large':
        return `${file.name} is larger than this server accepts.`
      case 'storage_unavailable':
        return 'The server could not reach its file storage. Try again in a moment.'
      case 'forbidden':
        return 'You do not have permission to upload files here.'
      case 'not_found':
        return 'That conversation no longer exists.'
      default:
        return error.message
    }
  }

  // A rejected `fetch` is a native TypeError, never an ApiError.
  if (error instanceof Error && error.message !== '') return error.message
  return 'The upload failed.'
}

// ── The hook ────────────────────────────────────────────────────────────────

export function useUploads(roomId: string): UploadsApi {
  const { server } = useAuth()
  const maxBytes = server?.max_upload_bytes ?? 0

  const [items, setItems] = useState<UploadItem[]>([])

  // The list is mirrored in a ref so that the async workers and the retry
  // handler read the current entries rather than whatever was captured when
  // their closure was created. Every mutation goes through `apply`, so the two
  // cannot drift.
  const itemsRef = useRef<UploadItem[]>([])
  const controllers = useRef(new Map<string, AbortController>())

  const apply = useCallback((update: (current: UploadItem[]) => UploadItem[]): void => {
    itemsRef.current = update(itemsRef.current)
    setItems(itemsRef.current)
  }, [])

  const patch = useCallback(
    (id: string, changes: Partial<UploadItem>): void => {
      apply((current) =>
        current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
      )
    },
    [apply],
  )

  const run = useCallback(
    async (id: string, file: File, stored: Attachment | null = null): Promise<void> => {
      const controller = new AbortController()
      controllers.current.set(id, controller)

      // `stored` is an attachment whose bytes already arrived: a retry after
      // the confirmation alone failed picks up at step 3.
      let attachment: Attachment | null = stored
      let finalizing = stored !== null

      try {
        if (attachment === null) {
          patch(id, { state: 'creating', progress: 0, error: null, retryable: false, resume: null })

          const session = await api.post<UploadSession>(
            `/rooms/${roomId}/uploads`,
            { filename: file.name, mime_type: mimeTypeOf(file), byte_size: file.size },
            controller.signal,
          )

          attachment = session.attachment
          patch(id, { state: 'uploading', attachment })

          // The XHR fires progress far more often than the list needs to
          // re-render, so only a whole percentage point is worth a state update.
          let lastPercent = -1
          await api.upload(
            uploadTarget(session.upload.url),
            file,
            session.upload.headers,
            (fraction) => {
              const percent = Math.round(fraction * 100)
              if (percent === lastPercent) return
              lastPercent = percent
              patch(id, { progress: fraction })
            },
            controller.signal,
          )
        }

        finalizing = true
        patch(id, { state: 'finalizing', progress: 1, error: null, retryable: false, resume: null })

        const checksum = await computeChecksum(file, controller.signal)
        const body: FinalizeUploadRequest = checksum === null ? {} : { checksum }

        const finalized = await api.post<Attachment>(
          `/uploads/${attachment.id}/finalize`,
          body,
          controller.signal,
          { timeoutMs: FINALIZE_TIMEOUT_MS },
        )

        patch(id, { state: 'ready', attachment: finalized, progress: 1, error: null, retryable: false, resume: null })
      } catch (error) {
        // Once finalize has been reached the bytes are on the server, and a
        // fault from here — a timeout, a 5xx, a dropped connection — leaves
        // only the confirmation to repeat. A refusal is final either way.
        const resume = finalizing && attachment !== null && !isRefusal(error) ? 'finalize' : null

        if (isAbortError(error)) {
          patch(id, { state: 'cancelled', progress: 0, error: null, retryable: true, resume })
          return
        }
        patch(id, { state: 'failed', error: describeFailure(error, file), retryable: true, resume })
      } finally {
        controllers.current.delete(id)
      }
    },
    [patch, roomId],
  )

  const addFiles = useCallback(
    (files: FileList | readonly File[]): void => {
      const incoming = Array.from(files)
      if (incoming.length === 0) return

      const created = incoming.map<UploadItem>((file) => {
        // Checked here as well as on the server so that dropping a 4 GB file
        // fails at once instead of after four minutes of transfer.
        const oversized = maxBytes > 0 && file.size > maxBytes

        return {
          id: localId(),
          file,
          name: file.name,
          size: file.size,
          mimeType: mimeTypeOf(file),
          state: oversized ? 'failed' : 'queued',
          progress: 0,
          attachment: null,
          error: oversized
            ? `${file.name} is ${formatBytes(file.size)}. This server accepts files up to ${formatBytes(maxBytes)}.`
            : null,
          retryable: false,
          resume: null,
        }
      })

      apply((current) => [...current, ...created])

      for (const item of created) {
        if (item.state === 'queued') void run(item.id, item.file)
      }
    },
    [apply, maxBytes, run],
  )

  const cancel = useCallback((id: string): void => {
    controllers.current.get(id)?.abort()
  }, [])

  const retry = useCallback(
    (id: string): void => {
      const item = itemsRef.current.find((entry) => entry.id === id)
      if (item === undefined || !item.retryable) return
      if (ACTIVE_STATES.has(item.state)) return

      void run(item.id, item.file, item.resume === 'finalize' ? item.attachment : null)
    },
    [run],
  )

  const remove = useCallback(
    (id: string): void => {
      controllers.current.get(id)?.abort()
      apply((current) => current.filter((item) => item.id !== id))
    },
    [apply],
  )

  const clear = useCallback((): void => {
    for (const controller of controllers.current.values()) controller.abort()
    controllers.current.clear()
    apply(() => [])
  }, [apply])

  // A tray belongs to one room. Carrying half-finished uploads into another
  // conversation would attach them to the wrong place.
  const previousRoom = useRef(roomId)
  useEffect(() => {
    if (previousRoom.current === roomId) return
    previousRoom.current = roomId
    clear()
  }, [clear, roomId])

  useEffect(() => {
    const inFlight = controllers.current
    return () => {
      for (const controller of inFlight.values()) controller.abort()
      inFlight.clear()
    }
  }, [])

  const readyIds = useMemo(
    () =>
      items.flatMap((item) =>
        item.state === 'ready' && item.attachment !== null ? [item.attachment.id] : [],
      ),
    [items],
  )

  const busy = useMemo(() => items.some((item) => ACTIVE_STATES.has(item.state)), [items])
  const failed = useMemo(
    () => items.some((item) => item.state === 'failed' || item.state === 'cancelled'),
    [items],
  )

  return { items, readyIds, busy, failed, addFiles, cancel, retry, remove, clear }
}

// ── Icons ───────────────────────────────────────────────────────────────────

function CloseIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function FileGlyph({ mimeType }: { mimeType: string }): ReactElement {
  const family = mimeType.split('/')[0] ?? ''

  if (family === 'image') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="7.5" cy="8" r="1.4" fill="currentColor" />
        <path d="m3.5 14 4-4 3.5 3.5 2.5-2 3 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      </svg>
    )
  }

  if (family === 'video') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <rect x="2.75" y="4.75" width="14.5" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.4" />
        <path d="M8.5 8.2v3.6L12 10z" fill="currentColor" />
      </svg>
    )
  }

  if (family === 'audio') {
    return (
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
        <path d="M12.5 3.5v9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M12.5 5.5 16.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        <circle cx="10" cy="13.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M11.5 2.75H6.5a1.75 1.75 0 0 0-1.75 1.75v11a1.75 1.75 0 0 0 1.75 1.75h7a1.75 1.75 0 0 0 1.75-1.75V6.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M11.5 2.75V6.5h3.75" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  )
}

// ── Tray ────────────────────────────────────────────────────────────────────

function statusLabel(item: UploadItem): string {
  switch (item.state) {
    case 'queued':
      return 'Waiting'
    case 'creating':
      return 'Preparing'
    case 'uploading':
      return `${Math.round(item.progress * 100)}%`
    case 'finalizing':
      return 'Finishing'
    case 'ready':
      return item.attachment?.status === 'scanning' ? 'Checking file' : 'Ready'
    case 'cancelled':
      return 'Cancelled'
    case 'failed':
      return 'Failed'
    default:
      return ''
  }
}

export interface UploadTrayProps {
  uploads: UploadsApi
  className?: string
}

/**
 * Sits above the composer. Renders nothing at all when there is nothing to
 * show, so the composer keeps its resting height.
 */
export function UploadTray({ uploads, className }: UploadTrayProps): ReactElement | null {
  const { items, cancel, retry, remove } = uploads

  if (items.length === 0) return null

  const announcement = items
    .filter((item) => item.state === 'failed' && item.error !== null)
    .map((item) => `${item.name}: ${item.error ?? ''}`)
    .join('. ')

  return (
    <div className={cx(styles.tray, className)}>
      <ul className={styles.list} aria-label="Files to attach">
        {items.map((item) => {
          const active = ACTIVE_STATES.has(item.state)
          const scanning = item.state === 'ready' && item.attachment?.status === 'scanning'

          return (
            <li
              key={item.id}
              className={cx(styles.item, item.state === 'failed' && styles.itemFailed)}
            >
              <span className={styles.glyph} aria-hidden="true">
                <FileGlyph mimeType={item.mimeType} />
              </span>

              <div className={styles.body}>
                <span className={styles.name} title={item.name}>
                  {item.name}
                </span>

                <span className={styles.meta}>
                  {formatBytes(item.size)}
                  <span aria-hidden="true"> · </span>
                  {statusLabel(item)}
                  {item.state === 'creating' || item.state === 'finalizing' || scanning ? (
                    <Spinner size="sm" className={styles.metaSpinner} />
                  ) : null}
                </span>

                {item.state === 'uploading' ? (
                  <progress
                    className={styles.progress}
                    value={item.progress}
                    max={1}
                    aria-label={`Uploading ${item.name}`}
                  />
                ) : null}

                {item.error !== null ? <p className={styles.error}>{item.error}</p> : null}
              </div>

              <div className={styles.actions}>
                {item.state === 'uploading' ? (
                  <Button size="sm" variant="ghost" onClick={() => cancel(item.id)}>
                    Cancel
                  </Button>
                ) : null}

                {item.retryable && !active ? (
                  <Button size="sm" variant="ghost" onClick={() => retry(item.id)}>
                    Retry
                  </Button>
                ) : null}

                <IconButton
                  size="sm"
                  label={`Remove ${item.name}`}
                  onClick={() => remove(item.id)}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            </li>
          )
        })}
      </ul>

      {/* Failures are the one thing here worth reading out; progress is not. */}
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  )
}
